import React from 'react';
import { PropTypes } from 'prop-types';
import { injectIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Accordion from 'components/Common/Accordion';
import Button from 'components/Common/Buttons';
import LocalisedMessage from 'components/Common/LocalisedMessage';

const ValidationResults = ({ result, showResult, dirty, onClick, onUploadDocumentClick, onSignAndSubmit, intl, previewOnlyMode }) => {
  function signSubmitButtonText() {
    const messageId = previewOnlyMode ? 'signSubmit.previewOnlyMode.buttonText' : 'signSubmit.buttonText';
    return intl.formatMessage({ id: messageId });
  }

  function signSubmitDescription() {
    const messageId = previewOnlyMode ? 'signSubmit.previewOnlyMode.description' : 'signSubmit.description';
    return <LocalisedMessage id={ messageId } />;
  }

  function title() {
    if (result != null) {
      if (result.valid) {
        if (!dirty) {
          return <div>
            <span className="text-bold">Results</span>
            <span className="title-rejections valid">
              <span className="check fa-layers fa-2x">
                <FontAwesomeIcon icon="circle" />
                <span className="fa-sm"><FontAwesomeIcon icon="check" className="layered-button fa-sm" /></span>
              </span>
              { result.rejections != null && result.rejections.length } <LocalisedMessage id="validationResult.rejectionsHint" />s
            </span>
          </div>;
        }
        return <div>
          <span className="text-bold">Results</span>
          <span className="title-rejections invalid">
            <span className="cross fa-layers fa-2x">
              <FontAwesomeIcon icon="circle" />
              <span className="fa-sm"><FontAwesomeIcon icon="times" className="layered-button fa-sm" /></span>
            </span>
            Press &apos;<LocalisedMessage id="validationResult.check.buttonText" />&apos;
          </span>
        </div>;
      }
      return <div>
        <span className="text-bold">Results</span>
        <span className="title-rejections invalid">
          <span className="cross fa-layers fa-2x">
            <FontAwesomeIcon icon="circle" />
            <span className="fa-sm"><FontAwesomeIcon icon="times" className="layered-button fa-sm" /></span>
          </span>
          { result.rejections != null && result.rejections.length } <LocalisedMessage id="validationResult.rejectionsHint" />{ `${ result.rejections.length !== 1 ? 's' : '' }` }
        </span>
      </div>;
    }
    return <div><span className="text-bold">No Results - Press &apos;<LocalisedMessage id="validationResult.check.buttonText" />&apos;</span></div>;
  }

  return <div id="validation-results" className="validation-results">
    <Accordion
      title={ title() }
      openIcon="chevron-up"
      closeIcon="chevron-down"
      className="left"
      isOpen={ showResult }
      onClick={ onClick }
    >
      <React.Fragment>
        <div className="row">
          <div className="col text-right">
            <Button value="Upload Document" className="btn-upload-link mb-2" onClick={ () => onUploadDocumentClick() } />
          </div>
        </div>
        <div className="row results-scroll">
          <div className="col">
            <div className="row">
              <div className="col-12 col-md-6 rejections-list">
                { result && result.rejections && result.rejections.length === 0 && !dirty && <React.Fragment>
                  <p className="text-sm">{ signSubmitDescription() }</p>
                  { (onSignAndSubmit != null) && <Button id="sign-submit-validation-button" value={ signSubmitButtonText() } onClick={ () => onSignAndSubmit() } /> }
                </React.Fragment> }
                { result && result.rejections && result.rejections.length === 0 && dirty
                && <p>There have been changes made to the form, please close this panel and press &apos;<LocalisedMessage id="validationResult.check.buttonText" />&apos; again to see if there are any more changes needed.</p>}
                { result && result.rejections && result.rejections.length > 0 && <React.Fragment>
                  <h3 className="cs-error"><LocalisedMessage id="validationResult.rejectionsTitle" /></h3>
                  <ul>
                    { result.rejections.map(item => <li key={ item.code }>{item.codeDescription}</li>) }
                  </ul>
                </React.Fragment> }
              </div>
              <div className="col-12 col-md-6 warnings-list">
                { result && result.warnings && result.warnings.filter(item => item.code.substring(0, 3) === 'WRN').length > 0 && <React.Fragment>
                  <h3 className="cp-primary"><LocalisedMessage id="validationResult.warningsTitle" /></h3>
                  <ul>
                    { result.warnings.map(item => item.code.substring(0, 3) === 'WRN'
                    && <li key={ item.code }>{item.codeDescription}</li>) }
                  </ul>
                </React.Fragment> }
              </div>
            </div>
            { result && result.warnings && result.warnings.filter(item => item.code.substring(0, 3) === 'REC').length > 0 && <div className="row">
              <div className="col-12 recommendations-list">
                <h3 className="cp-main"><LocalisedMessage id="validationResult.recommendationsTitle" /></h3>
                <ul>
                  { result.warnings.map(item => item.code.substring(0, 3) === 'REC'
                  && <li key={ item.code }>{item.codeDescription}</li>) }
                </ul>
              </div>
            </div> }
          </div>
        </div>
      </React.Fragment>
    </Accordion>
  </div>;
};

ValidationResults.propTypes = {
  result: PropTypes.shape({
    valid: PropTypes.bool,
    formType: PropTypes.string,
    rejections: PropTypes.arrayOf(PropTypes.shape()),
    warnings: PropTypes.arrayOf(PropTypes.shape())
  }),
  showResult: PropTypes.bool.isRequired,
  dirty: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onSignAndSubmit: PropTypes.func,
  onUploadDocumentClick: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  previewOnlyMode: PropTypes.bool.isRequired
};

ValidationResults.defaultProps = {
  result: null,
  dirty: false,
  onSignAndSubmit: null
};

export default injectIntl(ValidationResults);
